import { createContext, type ReactNode, useCallback, useContext, useEffect, useMemo, useState } from "react";

import { useInViewportLocations } from "~/features/recordGeoItems/hooks/useInViewportLocations";
import type { ComparisonArea, Location, PinnedLocation, ViewportType } from "~/lib/map/types";

const LS_KEYS = {
  comparisonAreas: "v2.areaInput.comparisonAreas",
  pinnedLocations: "v2.areaInput.pinnedLocations",
  isActiveInViewportLocations: "v2.areaInput.isActiveInViewportLocations",
} as const;

function readLS<T>(key: string, fallback: T): T {
  try {
    const raw = window.localStorage.getItem(key);
    return raw ? (JSON.parse(raw) as T) : fallback;
  } catch {
    return fallback;
  }
}

function writeLS<T>(key: string, value: T): void {
  try {
    window.localStorage.setItem(key, JSON.stringify(value));
  } catch {
    // ignore quota / serialization errors
  }
}

// Labels saved as a bare array predate the GeoItem label rule and may hold a link heading or a Record
// name, so they are not trusted as place names: the map shows the dot alone, lists show the GeoItem ID.
const LABEL_RULE_VERSION = 1;

type Versioned<T> = { version: typeof LABEL_RULE_VERSION; items: T[] };

function readVersionedLS<T>(key: string, untrustLabel: (legacy: T) => T): T[] {
  const stored = readLS<Versioned<T> | T[]>(key, []);
  return Array.isArray(stored) ? stored.map(untrustLabel) : stored.items;
}

function writeVersionedLS<T>(key: string, items: T[]): void {
  writeLS<Versioned<T>>(key, { version: LABEL_RULE_VERSION, items });
}

interface EditMapToolsContextValue {
  comparisonAreas: ComparisonArea[];
  setComparisonAreas: (areas: ComparisonArea[]) => void;
  pinnedLocations: PinnedLocation[];
  setPinnedLocations: (locations: PinnedLocation[]) => void;
  searchedLocation: Location | null;
  setSearchedLocation: (location: Location | null) => void;
  inViewportLocations: Location[];
  isActiveInViewportLocations: boolean;
  setIsActiveInViewportLocations: (isActive: boolean) => void;
  setViewport: (viewport: ViewportType) => void;
}

const EditMapToolsContext = createContext<EditMapToolsContextValue | null>(null);

export function useEditMapTools(): EditMapToolsContextValue {
  const ctx = useContext(EditMapToolsContext);
  if (!ctx) throw new Error("useEditMapTools must be used within an EditMapToolsProvider");
  return ctx;
}

export function EditMapToolsProvider({ children }: { children: ReactNode }) {
  const [viewport, setViewport] = useState<ViewportType | null>(null);
  const [comparisonAreas, setComparisonAreasState] = useState<ComparisonArea[]>(() =>
    readVersionedLS<ComparisonArea>(LS_KEYS.comparisonAreas, (area) => ({ ...area, label: area.geoItemId })),
  );
  const [pinnedLocations, setPinnedLocationsState] = useState<PinnedLocation[]>(() =>
    readVersionedLS<PinnedLocation>(LS_KEYS.pinnedLocations, (pin) => ({ ...pin, label: "" })),
  );
  const [searchedLocation, setSearchedLocation] = useState<Location | null>(null);
  const [inViewportLocations, setInViewportLocations] = useState<Location[]>([]);
  const [isActiveInViewportLocations, setIsActiveState] = useState<boolean>(() =>
    readLS(LS_KEYS.isActiveInViewportLocations, false),
  );

  const { updateLocations } = useInViewportLocations({
    isActive: isActiveInViewportLocations,
    viewport,
    onLocationsUpdate: setInViewportLocations,
  });

  const setComparisonAreas = useCallback((areas: ComparisonArea[]) => {
    setComparisonAreasState(areas);
    writeVersionedLS(LS_KEYS.comparisonAreas, areas);
  }, []);

  const setPinnedLocations = useCallback((locations: PinnedLocation[]) => {
    setPinnedLocationsState(locations);
    writeVersionedLS(LS_KEYS.pinnedLocations, locations);
  }, []);

  const setIsActiveInViewportLocations = useCallback((isActive: boolean) => {
    setIsActiveState(isActive);
    writeLS(LS_KEYS.isActiveInViewportLocations, isActive);
  }, []);

  useEffect(() => {
    if (isActiveInViewportLocations && viewport) void updateLocations();
  }, [viewport, isActiveInViewportLocations, updateLocations]);

  const value = useMemo<EditMapToolsContextValue>(
    () => ({
      comparisonAreas,
      setComparisonAreas,
      pinnedLocations,
      setPinnedLocations,
      searchedLocation,
      setSearchedLocation,
      inViewportLocations: isActiveInViewportLocations ? inViewportLocations : [],
      isActiveInViewportLocations,
      setIsActiveInViewportLocations,
      setViewport,
    }),
    [
      comparisonAreas,
      setComparisonAreas,
      pinnedLocations,
      setPinnedLocations,
      searchedLocation,
      inViewportLocations,
      isActiveInViewportLocations,
      setIsActiveInViewportLocations,
    ],
  );

  return <EditMapToolsContext.Provider value={value}>{children}</EditMapToolsContext.Provider>;
}
